import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Chip,
  Button,
  TextField,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Alert,
  useTheme,
  alpha,
  Autocomplete,
  Stack,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tabs,
  Tab,
  InputAdornment
} from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Description as DescriptionIcon,
  ArrowForward as ArrowForwardIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  UnfoldMore as UnfoldMoreIcon,
  UnfoldLess as UnfoldLessIcon,
  Save as SaveIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import projectsService from '../services/projectsService';
import LoadingAnimation from '../components/LoadingAnimation';
import { RevisionName, Party, WorkflowStep, Revision, MDRData, Project } from '../types';
import MDRTableAgGrid from '../components/MDRTableAgGrid';
import MDRTableReactDataGrid from '../components/MDRTableReactDataGrid';
import { StatusChip, WorkflowSteps, InlineWorkflowEditor } from '../components/MDRComponents';
import { useTabSearchParam } from '../hooks/useTabSearchParam';

// --- Helper Components ---
// Moved to ../components/MDRComponents.tsx

const EditableDateCell = ({ value, onSave }: { value: string | null, onSave: (date: string | null) => Promise<void> }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [date, setDate] = useState(value || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(date || null);
      setIsEditing(false);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDate(value || '');
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <TextField
          type="date"
          size="small"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          sx={{
            width: 140,
            '& .MuiInputBase-input': { py: 0.25, fontSize: '0.875rem' }
          }}
          autoFocus
        />
        <IconButton size="small" onClick={handleSave} disabled={saving} color="primary" sx={{ p: 0.25 }}>
          <CheckIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={handleCancel} disabled={saving} color="error" sx={{ p: 0.25 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    );
  }

  return (
    <Box display="flex" alignItems="center" gap={1} sx={{ minHeight: 24 }}>
      <Typography variant="body2">
        {value ? new Date(value).toLocaleDateString() : '-'}
      </Typography>
      <Tooltip title="Edit Expected Issue Date">
        <IconButton size="small" onClick={() => setIsEditing(true)} sx={{ opacity: 0.5, '&:hover': { opacity: 1 }, p: 0.25 }}>
          <EditIcon fontSize="small" sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const NewRevisionRow = ({
  revisionNames,
  parties,
  onSave,
  onCancel
}: {
  revisionNames: RevisionName[],
  parties: Party[],
  onSave: (data: any) => Promise<void>,
  onCancel: () => void
}) => {
  const [revisionNameId, setRevisionNameId] = useState<number | ''>('');
  const [step, setStep] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [workflow, setWorkflow] = useState<WorkflowStep[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleSave = async () => {
    if (!revisionNameId || !step) return;
    await onSave({
      revision_name_id: revisionNameId,
      step: parseInt(step),
      expected_issue_date: expectedDate || undefined,
      workflow
    });
  };

  return (
    <TableRow sx={{ bgcolor: 'action.hover' }}>
      <TableCell>
        <Stack direction="row" spacing={1}>
          <Autocomplete
            size="small"
            sx={{ minWidth: 150 }}
            options={revisionNames}
            getOptionLabel={(option) => option.name}
            value={revisionNames.find(r => r.id === revisionNameId) || null}
            onChange={(_, newValue) => setRevisionNameId(newValue?.id || '')}
            renderInput={(params) => <TextField {...params} label="Rev Name" />}
          />
          <TextField
            size="small"
            placeholder="Step"
            type="number"
            value={step}
            onChange={(e) => setStep(e.target.value)}
            sx={{ width: 70 }}
          />
        </Stack>
      </TableCell>
      <TableCell>
        <StatusChip status="pending" />
      </TableCell>
      <TableCell>
        <TextField
          type="date"
          size="small"
          value={expectedDate}
          onChange={(e) => setExpectedDate(e.target.value)}
          sx={{ width: 140 }}
        />
      </TableCell>
      <TableCell>-</TableCell>
      <TableCell>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{ textTransform: 'none', minWidth: 100 }}
          >
            {workflow.length > 0 ? `${workflow.length} Steps` : 'Set Workflow'}
          </Button>
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <InlineWorkflowEditor
              initialWorkflow={workflow}
              parties={parties}
              onSave={(newWorkflow) => {
                setWorkflow(newWorkflow);
                setAnchorEl(null);
              }}
              onCancel={() => setAnchorEl(null)}
            />
          </Popover>
          <IconButton size="small" color="primary" onClick={handleSave} disabled={!revisionNameId || !step}>
            <SaveIcon />
          </IconButton>
          <IconButton size="small" color="error" onClick={onCancel}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </TableCell>
    </TableRow>
  );
};

// --- Main Row Component ---

const DocumentRow = ({
  row,
  isExpanded,
  onToggle,
  onUpdateDate,
  revisionNames,
  parties,
  onAddRevision,

}: {
  row: MDRData,
  isExpanded: boolean,
  onToggle: () => void,
  onUpdateDate: (docId: number, revId: number, date: string | null) => Promise<void>,
  revisionNames: RevisionName[],
  parties: Party[],
  onAddRevision: (docId: number, data: any) => Promise<{ revision_id: number } | void>,

}) => {
  const [isAddingRevision, setIsAddingRevision] = useState(false);
  const theme = useTheme();




  return (
    <>
      <TableRow
        sx={{
          '& > *': { borderBottom: 'unset' },
          backgroundColor: isExpanded ? alpha(theme.palette.primary.main, 0.04) : 'inherit',
          transition: 'background-color 0.2s'
        }}
        hover
      >
        <TableCell width={50}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={onToggle}
          >
            {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row" sx={{ fontWeight: 600, color: 'primary.main' }}>
          {row.document_nd_number}
        </TableCell>
        <TableCell>{row.document_title}</TableCell>
        <TableCell>{row.document_type_name}</TableCell>
        <TableCell>{row.work_package_code}</TableCell>
        <TableCell>{row.work_unit_code}</TableCell>
        <TableCell align="right">
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={(e) => {
              e.stopPropagation();
              if (!isExpanded) onToggle();
              setIsAddingRevision(true);
            }}
            variant="outlined"
            sx={{ borderRadius: 4, textTransform: 'none' }}
          >
            Add Rev
          </Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1, ml: 4, p: 1, bgcolor: alpha(theme.palette.background.paper, 0.5), borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="subtitle2" gutterBottom component="div" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DescriptionIcon fontSize="small" /> Revisions History
              </Typography>
              <Table size="small" aria-label="revisions" sx={{ '& td, & th': { padding: '4px 8px' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell width="15%">Revision</TableCell>
                    <TableCell width="15%">Status</TableCell>
                    <TableCell width="20%">Expected Issue Date</TableCell>
                    <TableCell width="15%">Issued Date</TableCell>
                    <TableCell width="35%">Workflow</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.revisions.map((rev) => (
                    <TableRow key={rev.revision_id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell component="th" scope="row">
                        <Chip label={rev.revision_name_step} size="small" color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} />
                      </TableCell>
                      <TableCell>
                        <StatusChip status={rev.status} />
                      </TableCell>
                      <TableCell>
                        <EditableDateCell
                          value={rev.expected_issue_date}
                          onSave={(date) => onUpdateDate(row.document_id, rev.revision_id, date)}
                        />
                      </TableCell>
                      <TableCell>{rev.issued_date ? new Date(rev.issued_date).toLocaleDateString() : '-'}</TableCell>
                      <TableCell>
                        <WorkflowSteps
                          workflow={rev.workflow}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {isAddingRevision && (
                    <NewRevisionRow
                      revisionNames={revisionNames}
                      parties={parties}
                      onSave={async (data) => {
                        await onAddRevision(row.document_id, data);
                        setIsAddingRevision(false);
                      }}
                      onCancel={() => setIsAddingRevision(false)}
                    />
                  )}
                  {row.revisions.length === 0 && !isAddingRevision && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary', fontStyle: 'italic' }}>
                        No revisions found for this document.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* Workflow Popover for Existing Revisions */}

    </>
  );
};

// --- Main Page Component ---

const MDRPage: React.FC = () => {
  const [mdrData, setMdrData] = useState<MDRData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  const [revisionNames, setRevisionNames] = useState<RevisionName[]>([]);
  const [parties, setParties] = useState<Party[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [tabValue, setTabValue] = useTabSearchParam(
    ['list', 'ag-grid', 'react-data-grid'] as const,
    'list',
  );

  const handleTabChange = (_event: React.SyntheticEvent, newValue: 'list' | 'ag-grid' | 'react-data-grid') => {
    setTabValue(newValue);
  };

  useEffect(() => {
    loadMDRData();
    loadMetadata();
  }, []);

  const loadMetadata = async () => {
    try {
      const [revNames, partiesData, projectsData] = await Promise.all([
        projectsService.getRevisionNames(),
        projectsService.getParties(),
        projectsService.getProjects()
      ]);
      setRevisionNames(revNames);
      setParties(partiesData);
      setProjects(projectsData);
    } catch (err) {
      console.error('Error loading metadata:', err);
    }
  };

  const loadMDRData = async () => {
    try {
      setLoading(true);
      const data = await projectsService.getMDR();
      setMdrData(data);
      setError(null);
    } catch (err) {
      setError('Failed to load Master Document Register data');
      console.error('Error loading MDR data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDate = useCallback(async (docId: number, revId: number, date: string | null) => {
    try {
      await (projectsService as any).updateMDRRevision(docId, revId, date);

      // Optimistic update
      setMdrData(prev => prev.map(doc => {
        if (doc.document_id === docId) {
          return {
            ...doc,
            revisions: doc.revisions.map(rev => {
              if (rev.revision_id === revId) {
                return { ...rev, expected_issue_date: date };
              }
              return rev;
            })
          };
        }
        return doc;
      }));
    } catch (error) {
      console.error('Failed to update date:', error);
    }
  }, []);

  const handleAddRevision = async (docId: number, data: any) => {
    try {
      const doc = mdrData.find(d => d.document_id === docId);
      if (!doc) return;

      // Check if we have pre-structured data (from CreateDocumentRevisionDialog)
      // or if we need to transform from workflow steps (legacy inline editor)
      let checkers = data.checkers || [];
      let approvers = data.approvers || [];
      let authorPartyId = data.author_party_id;

      if (data.workflow) {
        // Transform workflow to checkers/approvers/author
        data.workflow.forEach((step: WorkflowStep, index: number) => {
          if (step.role === 'checker') {
            checkers.push({ party_id: step.party_id, step_order: index + 1 });
          } else if (step.role === 'approver') {
            approvers.push({ party_id: step.party_id, step_order: index + 1 });
          } else if (step.role === 'author') {
            authorPartyId = step.party_id;
          }
        });
      }

      const result = await projectsService.createDocumentRevision(doc.project_id, docId, {
        document_id: docId,
        revision_name_id: data.revision_name_id,
        step: data.step,
        expected_issue_date: data.expected_issue_date,
        status: 'pending',
        author_party_id: authorPartyId,
        checkers,
        approvers,
        backup_parties: data.backup_parties || []
      });

      loadMDRData();
      return { revision_id: result.revision_id };
    } catch (error) {
      console.error('Failed to add revision:', error);
      alert('Failed to add revision');
    }
  };



  const handleToggle = (id: number) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIds(newExpanded);
  };

  const handleExpandAll = () => {
    setExpandedIds(new Set(filteredData.map(d => d.document_id)));
  };

  const handleCollapseAll = () => {
    setExpandedIds(new Set());
  };

  const filteredData = useMemo(() => {
    if (!searchQuery) return mdrData;
    const lowerQuery = searchQuery.toLowerCase();
    return mdrData.filter(doc =>
      doc.document_nd_number.toLowerCase().includes(lowerQuery) ||
      doc.document_title.toLowerCase().includes(lowerQuery) ||
      doc.work_package_code.toLowerCase().includes(lowerQuery) ||
      doc.work_unit_code.toLowerCase().includes(lowerQuery)
    );
  }, [mdrData, searchQuery]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <LoadingAnimation />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', px: 1 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center">
          <DescriptionIcon sx={{ mr: 2, color: 'primary.main', fontSize: '2rem' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 500 }}>
            Master Document Register
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          {tabValue === 'list' && (
            <>
              <Button
                variant="outlined"
                onClick={handleExpandAll}
                startIcon={<UnfoldMoreIcon />}
              >
                Expand All
              </Button>
              <Button
                variant="outlined"
                onClick={handleCollapseAll}
                startIcon={<UnfoldLessIcon />}
              >
                Collapse All
              </Button>
            </>
          )}
          <Tooltip title="Refresh data">
            <IconButton onClick={loadMDRData} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary">
          <Tab label="List View" value="list" />
          <Tab label="AG Grid" value="ag-grid" />
          <Tab label="React Data Grid" value="react-data-grid" />
        </Tabs>
      </Paper>

      {tabValue === 'list' && (
        <>
          <Paper sx={{ mb: 3, p: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by ND Number, Title, Work Package..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Paper>

          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table size="small" aria-label="collapsible table">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'action.hover' }}>
                  <TableCell width={40} />
                  <TableCell sx={{ fontWeight: 'bold' }}>ND Number</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Work Package</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Work Unit</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.map((row) => (
                  <DocumentRow
                    key={row.document_id}
                    row={row}
                    isExpanded={expandedIds.has(row.document_id)}
                    onToggle={() => handleToggle(row.document_id)}
                    onUpdateDate={handleUpdateDate}
                    revisionNames={revisionNames}
                    parties={parties}
                    onAddRevision={handleAddRevision}

                  />
                ))}
                {filteredData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No documents found matching your search.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'right' }}>
            Total Documents: {filteredData.length}
          </Typography>
        </>
      )}

      {tabValue === 'ag-grid' && (
        <Box sx={{ height: 'calc(100vh - 200px)', width: '100%' }}>
          <MDRTableAgGrid
            data={mdrData}
            projects={projects}
            revisionNames={revisionNames}
            parties={parties}
            onUpdateDate={handleUpdateDate}

            onAddRevision={handleAddRevision}
          />
        </Box>
      )}

      {tabValue === 'react-data-grid' && (
        <Box sx={{ height: 'calc(100vh - 200px)', width: '100%' }}>
          <MDRTableReactDataGrid
            data={mdrData}
            projects={projects}
            revisionNames={revisionNames}
            parties={parties}
            onUpdateDate={handleUpdateDate}

            onAddRevision={handleAddRevision}
          />
        </Box>
      )}
    </Box>
  );
};

export default MDRPage;